/// <amd-module name="Wit/QueriesHub/Charts/Common/ChartTemplate" />

import * as Resources from "Wit/QueriesHub/Resources";

/**
 * The different types of charts we can render.
 *
 * ("stackBarChart" is not a typo, it's kept that way for back-compat)
 */
export type ChartType = "PieChart" | "BarChart" | "stackBarChart" | "ColumnChart" | "PivotTable" | "StackAreaChart" | "AreaChart" | "LineChart";
/**
 * The different properties a chart can have.
 */
export type ChartProperty = "groupBy" | "measure" | "orderBy" | "series" | "historyRange";

/**
 * A chart template describes a chart type and its properties.
 */
export interface IChartTemplate {
    label: string;
    type: ChartType;
    properties: ChartProperty[];
}

const simpleProperties: ChartProperty[] = ["groupBy", "measure", "orderBy"];
const historicalProperties: ChartProperty[] = ["groupBy", "measure", "historyRange", "orderBy"];

/**
 * Maps ChartType to IChartTemplate.
 */
export const ChartTemplateMap: { [key in ChartType]: IChartTemplate } = {
    PieChart: {
        label: Resources.PieChartLabel,
        type: "PieChart",
        properties: simpleProperties
    },
    BarChart: {
        label: Resources.BarChartLabel,
        type: "BarChart",
        properties: simpleProperties
    },
    ColumnChart: {
        label: Resources.ColumnChartLabel,
        type: "ColumnChart",
        properties: simpleProperties
    },
    stackBarChart: {
        label: Resources.StackBarChartLabel,
        type: "stackBarChart",
        properties: ["series", ...simpleProperties]
    },
    PivotTable: {
        label: Resources.PivotTableLabel,
        type: "PivotTable",
        properties: ["series", ...simpleProperties]
    },
    StackAreaChart: {
        label: Resources.StackAreaChartLabel,
        type: "StackAreaChart",
        properties: historicalProperties
    },
    AreaChart: {
        label: Resources.AreaChartLabel,
        type: "AreaChart",
        properties: ["measure", "historyRange", "orderBy"]
    },
    LineChart: {
        label: Resources.LineChartLabel,
        type: "LineChart",
        properties: historicalProperties
    }
};

/**
 * List of all ChartTemplates.
 */
export const ChartTemplates: IChartTemplate[] = [
    ChartTemplateMap["PieChart"],
    ChartTemplateMap["BarChart"],
    ChartTemplateMap["ColumnChart"],
    ChartTemplateMap["stackBarChart"],
    ChartTemplateMap["PivotTable"],
    ChartTemplateMap["StackAreaChart"],
    ChartTemplateMap["AreaChart"],
    ChartTemplateMap["LineChart"]
];

/**
 * Properties that determine how a chart is rendered.
 */
export type ChartPropertyMap = {
    groupBy?: string;
    measure?: { aggregation: string; propertyName: string };
    orderBy?: { propertyName: string; direction: string };
    series?: string;
    historyRange?: string;
};

export type ChartPropertyType<T extends keyof ChartPropertyMap> = ChartPropertyMap[T];
